/**
 * Order Service
 * Service for managing order operations via Commerce Service API
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-004
 */

import type {
  OrderDetails,
  OrderSummary,
  OrderFilters,
  ApiResponse,
  PagedResponse,
  PaymentLinkResponse,
  PaymentStatusResponse,
} from '@/types/order';

// ============================================
// CONFIGURATION
// ============================================

// Use relative path for Vite dev proxy, absolute URL for production
const COMMERCE_SERVICE_BASE_URL = import.meta.env.PROD
  ? (import.meta.env.VITE_COMMERCE_SERVICE_URL || 'http://localhost:8083/commerce-service')
  : '/commerce-service';
const ORDER_API_PATH = '/api/v1/orders';

/**
 * Get JWT token from localStorage
 */
function getAuthToken(): string | null {
  const token = localStorage.getItem('saubhagya_jwt_token');
  console.log('[OrderService] JWT token check:', token ? 'Token exists' : 'No token found');
  return token;
}

/**
 * Validate JWT token exists
 */
function validateAuthToken(): void {
  const token = getAuthToken();
  if (!token) {
    throw new OrderApiError(
      'Authentication required. Please login again.',
      401
    );
  }
}

/**
 * Get authorization headers
 */
function getHeaders(): HeadersInit {
  const token = getAuthToken();
  // Token may already include "Bearer " prefix, so check before adding it
  const authHeader = token
    ? token.startsWith('Bearer ') ? token : `Bearer ${token}`
    : undefined;
  return {
    'Content-Type': 'application/json',
    ...(authHeader && { Authorization: authHeader }),
  };
}

// ============================================
// ERROR HANDLING
// ============================================

/**
 * Error codes for specific error types
 */
export enum OrderErrorCode {
  NETWORK_ERROR = 'NETWORK_ERROR',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  NOT_FOUND = 'NOT_FOUND',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  SERVER_ERROR = 'SERVER_ERROR',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  TIMEOUT = 'TIMEOUT',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Custom error class for API errors
 */
export class OrderApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public errorCode?: OrderErrorCode,
    public errors?: string[]
  ) {
    super(message);
    this.name = 'OrderApiError';
  }
}

/**
 * Parse error response from API
 */
async function parseErrorResponse(response: Response): Promise<string> {
  try {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const errorData = await response.json();

      if (errorData.message) {
        return errorData.message;
      }
      if (errorData.error) {
        return errorData.error;
      }
      if (errorData.errors && Array.isArray(errorData.errors)) {
        return errorData.errors.join(', ');
      }
    }

    return response.statusText || 'An error occurred';
  } catch {
    return 'An unexpected error occurred';
  }
}

/**
 * Handle API response and errors
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorMessage = await parseErrorResponse(response);

    let userMessage = errorMessage;
    let errorCode = OrderErrorCode.UNKNOWN;

    switch (response.status) {
      case 400:
        userMessage = `Validation Error: ${errorMessage}`;
        errorCode = OrderErrorCode.VALIDATION_ERROR;
        break;
      case 401:
        userMessage = 'Authentication required. Please login again.';
        errorCode = OrderErrorCode.UNAUTHORIZED;
        break;
      case 403:
        userMessage = 'You do not have permission to perform this action.';
        errorCode = OrderErrorCode.FORBIDDEN;
        break;
      case 404:
        userMessage = 'Order not found.';
        errorCode = OrderErrorCode.NOT_FOUND;
        break;
      case 409:
        userMessage = `Conflict: ${errorMessage}`;
        break;
      case 500:
        userMessage = 'Server error. Please try again later.';
        errorCode = OrderErrorCode.SERVER_ERROR;
        break;
      case 503:
        userMessage = 'Service unavailable. Please try again later.';
        errorCode = OrderErrorCode.SERVICE_UNAVAILABLE;
        break;
    }

    console.error('[OrderService] HTTP Error:', {
      status: response.status,
      errorCode,
      message: userMessage,
    });

    throw new OrderApiError(userMessage, response.status, errorCode);
  }

  try {
    return await response.json();
  } catch (parseError) {
    console.error('[OrderService] JSON parse error:', parseError);
    throw new OrderApiError('Invalid response format from server', 500, OrderErrorCode.SERVER_ERROR);
  }
}

// ============================================
// DEMO DATA HELPERS
// ============================================

/**
 * Generate demo order details for testing when backend is unavailable
 */
function generateDemoOrderDetails(orderId: number): OrderDetails {
  return {
    id: orderId,
    orderNumber: `ORD-2024-${String(orderId).padStart(5, '0')}`,
    orderDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    orderStatus: 'CONFIRMED',
    customer: {
      customerId: 1001,
      customerName: 'Demo Customer Pvt Ltd',
      email: 'customer@demo.com',
      phone: '+91-9876543210',
      gstNumber: '27AABCU9603R1ZX',
      panNumber: 'AABCU9603R',
    },
    contract: {
      contractId: 501,
      contractNumber: 'CNT-2024-001',
      contractDate: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
      contractStatus: 'ACTIVE',
      contractUrl: undefined,
    },
    gstBreakdown: {
      subtotal: 50000,
      totalDiscount: 2500,
      cgstAmount: 4275,
      sgstAmount: 4275,
      igstAmount: 0,
      totalTax: 8550,
      totalAmount: 56050,
    },
    lineItems: [
      {
        id: 1,
        orderLineNumber: 'LINE-001',
        productType: 'BIO_CNG',
        productName: 'Bio-CNG Grade A',
        description: 'High purity Bio-CNG with 95%+ methane content',
        quantity: 1000,
        quantityUnit: 'KG',
        unitPrice: 45,
        discountPercentage: 5,
        discountAmount: 2250,
        taxBreakdown: [
          { taxType: 'CGST', rate: 9, amount: 3823.5 },
          { taxType: 'SGST', rate: 9, amount: 3823.5 },
        ],
        totalTax: 7647,
        lineTotal: 50397,
        batchAllocationIds: [101, 102],
      },
      {
        id: 2,
        orderLineNumber: 'LINE-002',
        productType: 'SLURRY',
        productName: 'Organic Slurry Premium',
        description: 'Nutrient-rich organic slurry for fertilization',
        quantity: 500,
        quantityUnit: 'LITERS',
        unitPrice: 5,
        discountPercentage: 10,
        discountAmount: 250,
        taxBreakdown: [
          { taxType: 'CGST', rate: 9, amount: 202.5 },
          { taxType: 'SGST', rate: 9, amount: 202.5 },
        ],
        totalTax: 405,
        lineTotal: 2655,
        batchAllocationIds: [201],
      },
    ],
    delivery: {
      address: {
        addressLine1: '123 Industrial Area',
        addressLine2: 'Sector 15',
        city: 'Mumbai',
        state: 'Maharashtra',
        pincode: '400001',
        country: 'India',
        landmark: 'Near Highway Junction',
      },
      contactPerson: 'Rajesh Kumar',
      contactPhone: '+91-9123456789',
      expectedDeliveryDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0],
      specialInstructions: 'Handle with care. Deliver during business hours only (9 AM - 6 PM)',
    },
    statusTimeline: [
      {
        status: 'PENDING',
        timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        updatedBy: 'System',
        remarks: 'Order created',
      },
      {
        status: 'CONFIRMED',
        timestamp: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
        updatedBy: 'Sales Team',
        remarks: 'Order confirmed after payment verification',
      },
    ],
    invoices: [
      {
        invoiceId: '2001',
        invoiceNumber: 'INV-2024-001234',
        invoiceDate: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        invoiceAmount: 56050,
        status: 'GENERATED',
      },
    ],
    batchAllocations: [
      {
        allocationId: 1,
        batchId: '101',
        batchNumber: 'BATCH-CNG-2024-101',
        allocatedQuantityKg: 600,
        allocatedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        qualityGrade: 'GRADE_A',
        productionDate: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split('T')[0],
      },
      {
        allocationId: 2,
        batchId: '102',
        batchNumber: 'BATCH-CNG-2024-102',
        allocatedQuantityKg: 400,
        allocatedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        qualityGrade: 'GRADE_A',
        productionDate: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split('T')[0],
      },
      {
        allocationId: 3,
        batchId: '201',
        batchNumber: 'BATCH-SLURRY-2024-201',
        allocatedQuantityKg: 500,
        allocatedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        qualityGrade: 'STANDARD',
        productionDate: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split('T')[0],
      },
    ],
    payment: {
      paymentStatus: 'PARTIAL',
      totalAmount: 56050,
      paidAmount: 30000,
      balanceDue: 26050,
      lastPaymentDate: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
      nextDueDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0],
    },
    availableActions: ['VIEW_INVOICE', 'SCHEDULE_DELIVERY', 'CANCEL_ORDER', 'EDIT_ORDER'],
    createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    createdBy: 'Sales Admin',
    updatedAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
    updatedBy: 'Sales Team',
    remarks: 'Priority customer - handle with care',
    internalNotes: 'Customer has requested eco-friendly packaging',
  };
}

// ============================================
// BACKEND RESPONSE MAPPER
// ============================================

/**
 * Simple order DTO returned from /from-contract endpoint
 * This is the response structure for contract-based order creation
 */
interface CreateOrderResponseDTO {
  id: number;
  orderNumber: string;
  externalId: string;
  orderType: string;
  buyerId: string;
  buyerName: string;
  volumeCubicMeters: number;
  unitPrice: number;
  totalAmount: number;
  gstAmount: number;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  finalAmount: number;
  currency: string;
  status: OrderStatus;
  paymentTerms: string;
  orderDate: string;
  requiredDeliveryDate: string;
  actualDeliveryDate: string | null;
  deliveryAddress: string;
  specialRequirements: string | null;
  contactPersonName: string | null;
  contactNumber: string | null;
  contactEmail: string;
  createdAt: string;
  updatedAt: string;
  requiresTransport: boolean;
  transportBookingId: string | null;
  transportStatus: string | null;
  paymentStatus: string | null;
  paidAmount: number | null;
  outstandingAmount: number | null;
  overdue: boolean;
  completionPercentage: number;
  paymentCompletionPercentage: number;
  daysUntilDelivery: number;
}

/**
 * Map raw API response to full OrderDetails (defensive typing)
 * Handles multiple backend response formats for compatibility
 */
function mapRawOrderResponse(data: any): OrderDetails {
  console.log('[OrderService] Mapping raw order response:', data);

  // Safely convert amounts - they might come back as strings or numbers
  const toNumber = (val: any): number => {
    if (val === null || val === undefined) return 0;
    const num = typeof val === 'string' ? parseFloat(val) : Number(val);
    return isNaN(num) ? 0 : num;
  };

  // Handle both 'content' (paginated response) and direct order fields
  const orderData = data.content ? data.content[0] : data;

  // Extract amounts with fallbacks for different field names
  const totalAmount = toNumber(orderData.totalAmount || orderData.total_amount || 0);
  const gstAmount = toNumber(orderData.gstAmount || orderData.gst_amount || 0);
  const cgstAmount = toNumber(orderData.cgstAmount || orderData.cgst_amount || gstAmount * 0.5);
  const sgstAmount = toNumber(orderData.sgstAmount || orderData.sgst_amount || gstAmount * 0.5);
  const igstAmount = toNumber(orderData.igstAmount || orderData.igst_amount || 0);
  const finalAmount = toNumber(orderData.finalAmount || orderData.final_amount || totalAmount + gstAmount);
  const unitPrice = toNumber(orderData.unitPrice || orderData.unit_price || 0);
  const volumeCubicMeters = toNumber(orderData.volumeCubicMeters || orderData.volume_cubic_meters || 0);

  return {
    id: orderData.id || 0,
    orderNumber: orderData.orderNumber || '',
    orderDate: orderData.orderDate || new Date().toISOString(),
    orderStatus: (orderData.status || 'PENDING') as OrderStatus,

    customer: {
      customerId: 0,
      customerName: orderData.buyerName || '',
      email: orderData.contactEmail || '',
      phone: orderData.customerPhone || orderData.contactNumber || '',  // FIXED: Map customerPhone from API response
      gstNumber: undefined,
    },
    customerPhone: orderData.customerPhone || orderData.contactNumber || '',  // ADDED: Also include at root level

    gstBreakdown: {
      subtotal: totalAmount - gstAmount,
      totalDiscount: 0,
      cgstAmount,
      sgstAmount,
      igstAmount,
      totalTax: gstAmount,
      totalAmount: finalAmount,
    },

    lineItems: orderData.lineItems && orderData.lineItems.length > 0
      ? orderData.lineItems.map((item: any) => ({
          id: item.id,
          orderLineNumber: `LINE-${item.id}`,
          productType: 'BIO_CNG',
          productName: item.productName,
          quantity: item.quantity,
          quantityUnit: item.quantityUnit,
          unitPrice: toNumber(item.unitPrice),
          discountPercentage: item.discountPercentage || 0,
          discountAmount: item.discountAmount || 0,
          taxBreakdown: sgstAmount > 0
            ? [
                { taxType: 'CGST' as const, rate: 9, amount: cgstAmount },
                { taxType: 'SGST' as const, rate: 9, amount: sgstAmount },
              ]
            : [{ taxType: 'IGST' as const, rate: 18, amount: igstAmount }],
          totalTax: item.lineTotal - (item.quantity * toNumber(item.unitPrice)),
          lineTotal: item.lineTotal,
          batchCode: item.batchCode || undefined,  // ADDED: Map batch code from API response
        }))
      : [
          {
            id: 1,
            orderLineNumber: 'LINE-001',
            productType: 'BIO_CNG',
            productName: 'Bio-CNG',
            quantity: volumeCubicMeters,
            quantityUnit: 'CUBIC_METERS',
            unitPrice,
            discountPercentage: 0,
            discountAmount: 0,
            taxBreakdown: sgstAmount > 0
              ? [
                  { taxType: 'CGST' as const, rate: 9, amount: cgstAmount },
                  { taxType: 'SGST' as const, rate: 9, amount: sgstAmount },
                ]
              : [{ taxType: 'IGST' as const, rate: 18, amount: igstAmount }],
            totalTax: gstAmount,
            lineTotal: finalAmount,
            batchCode: undefined,  // ADDED: Include batchCode field
          },
        ],

    delivery: {
      address: {
        addressLine1: orderData.deliveryAddress || '',
        city: '',
        state: '',
        pincode: '',
        country: 'India',
      },
      contactPerson: orderData.contactPersonName || '',
      contactPhone: orderData.contactNumber || '',
      expectedDeliveryDate: orderData.requiredDeliveryDate || '',
      actualDeliveryDate: orderData.actualDeliveryDate || undefined,
      specialInstructions: orderData.specialRequirements || undefined,
    },

    statusTimeline: [
      {
        status: (orderData.status || 'PENDING') as OrderStatus,
        timestamp: orderData.orderDate || new Date().toISOString(),
        updatedBy: 'System',
        remarks: 'Order created',
      },
    ],

    payment: {
      paymentStatus: (orderData.paymentStatus || 'PENDING') as any,
      totalAmount: finalAmount,
      // paidAmount: Only use actual paidAmount field, NOT paymentAmount (which is the amount to pay)
      // If payment_status is PAID/COMPLETED, use finalAmount as paidAmount
      paidAmount: orderData.paymentStatus === 'PAID' || orderData.paymentStatus === 'COMPLETED'
        ? toNumber(orderData.paidAmount) || finalAmount
        : toNumber(orderData.paidAmount) || 0,
      // balanceDue: For PAID status, balance is 0; otherwise calculate from remaining amount
      balanceDue: orderData.paymentStatus === 'PAID' || orderData.paymentStatus === 'COMPLETED'
        ? 0
        : toNumber(orderData.outstandingAmount) || finalAmount - (toNumber(orderData.paidAmount) || 0),
      nextDueDate: orderData.requiredDeliveryDate || '',
      // Cashfree payment link fields
      paymentLink: orderData.paymentLink,
      cashfreeOrderId: orderData.cashfreeOrderId,
      paymentCompletedAt: orderData.paymentCompletedAt,
    },

    // Payment link fields at order level
    paymentLink: orderData.paymentLink,
    cashfreeOrderId: orderData.cashfreeOrderId,
    paymentCompletedAt: orderData.paymentCompletedAt,

    batchAllocations: [],

    // Transport tracking - map from API response fields
    transport: orderData.transportBookingId || orderData.transportRequestId ? {
      transportRequestId: orderData.transportRequestId || orderData.transportBookingId,
      transportRequestNumber: orderData.transportRequestNumber || `TR-${orderData.transportRequestId || orderData.transportBookingId}`,
      transportStatus: orderData.transportStatus || 'PENDING',
      transportPickedUpAt: orderData.transportPickedUpAt,
      transportDeliveredAt: orderData.transportDeliveredAt,
      transportCompletedAt: orderData.transportCompletedAt,
    } : undefined,

    createdAt: orderData.createdAt || new Date().toISOString(),
    createdBy: 'System',
    updatedAt: orderData.updatedAt || new Date().toISOString(),
    updatedBy: 'System',
  };
}

/**
 * Map simple CreateOrderResponseDTO to full OrderDetails for display
 */
function mapCreateOrderResponse(response: CreateOrderResponseDTO): OrderDetails {
  console.log('[OrderService] Mapping create order response:', response);

  return {
    id: response.id,
    orderNumber: response.orderNumber,
    orderDate: response.orderDate,
    orderStatus: response.status,

    customer: {
      customerId: 0,
      customerName: response.buyerName,
      email: response.contactEmail,
      phone: response.customerPhone || response.contactNumber || '',  // FIXED: Map customerPhone
      gstNumber: undefined,
    },
    customerPhone: response.customerPhone || response.contactNumber || '',  // ADDED: Include at root level

    gstBreakdown: {
      subtotal: response.totalAmount - response.gstAmount,
      totalDiscount: 0,
      cgstAmount: response.cgstAmount,
      sgstAmount: response.sgstAmount,
      igstAmount: response.igstAmount,
      totalTax: response.gstAmount,
      totalAmount: response.finalAmount,
    },

    lineItems: [
      {
        id: 1,
        orderLineNumber: 'LINE-001',
        productType: 'BIO_CNG',
        productName: 'Bio-CNG',
        quantity: response.volumeCubicMeters,
        quantityUnit: 'CUBIC_METERS',
        unitPrice: response.unitPrice,
        discountPercentage: 0,
        discountAmount: 0,
        taxBreakdown: response.sgstAmount > 0
          ? [
              { taxType: 'CGST' as const, rate: 9, amount: response.cgstAmount },
              { taxType: 'SGST' as const, rate: 9, amount: response.sgstAmount },
            ]
          : [{ taxType: 'IGST' as const, rate: 18, amount: response.igstAmount }],
        totalTax: response.gstAmount,
        lineTotal: response.finalAmount,
        batchCode: undefined,  // ADDED: Include batchCode field
      },
    ],

    delivery: {
      address: {
        addressLine1: response.deliveryAddress,
        city: '',
        state: '',
        pincode: '',
        country: 'India',
      },
      contactPerson: response.contactPersonName || '',
      contactPhone: response.contactNumber || '',
      expectedDeliveryDate: response.requiredDeliveryDate,
      actualDeliveryDate: response.actualDeliveryDate || undefined,
      specialInstructions: response.specialRequirements || undefined,
    },

    statusTimeline: [
      {
        status: response.status,
        timestamp: response.orderDate,
        updatedBy: 'System',
        remarks: 'Order created',
      },
    ],

    payment: {
      paymentStatus: (response.paymentStatus || 'PENDING') as any,
      totalAmount: response.finalAmount,
      paidAmount: response.paidAmount || 0,
      balanceDue: response.outstandingAmount || response.finalAmount,
      nextDueDate: response.requiredDeliveryDate,
    },

    batchAllocations: [],
    createdAt: response.createdAt,
    createdBy: 'System',
    updatedAt: response.updatedAt,
    updatedBy: 'System',
  };
}

/**
 * Backend DTO structure (from OrderDetailsDTO.java)
 */
interface BackendOrderDetailsDTO {
  orderHeader: {
    orderId: number;
    orderNumber: string;
    orderDate: string;
    status: OrderStatus;
    customerId: string;
    customerName: string;
    customerEmail: string;
    customerPhone: string;
    customerGstin?: string;
    contractNumber?: string;
    contractId?: string;
    totalAmount: number;
    gstAmount: number;
    cgstAmount?: number;
    sgstAmount?: number;
    igstAmount?: number;
    finalAmount: number;
    currency?: string;
    qualityGrade?: string;
    quantityCubicMeters?: number;
    unitPrice?: number;
  };
  lineItems: Array<{
    lineItemId: number;
    productName: string;
    batchNumber?: string;
    quantity: number;
    unit: string;
    unitPrice: number;
    discount: number;
    discountAmount: number;
    taxDetails?: {
      cgstRate: number;
      cgstAmount: number;
      sgstRate: number;
      sgstAmount: number;
      igstRate: number;
      igstAmount: number;
      totalTax: number;
    };
    lineTotal: number;
    deliveryStatus?: string;
    deliveredQuantity?: number;
    remainingQuantity?: number;
  }>;
  deliveryInfo?: {
    deliveryAddress: string;
    contactPerson: string;
    contactPhone: string;
    requestedDeliveryDate?: string;
    confirmedDeliveryDate?: string;
    actualDeliveryDate?: string;
    specialInstructions?: string;
    deliveryMode?: string;
    currentLocation?: string;
    deliveryStatus?: string;
  };
  statusTimeline?: {
    events: Array<{
      status: OrderStatus;
      statusLabel: string;
      timestamp: string;
      performedBy: string;
      remarks?: string;
      isCompleted: boolean;
      isCurrent: boolean;
    }>;
    currentStatus: string;
    completionPercentage: number;
  };
  associatedRecords?: {
    invoices?: Array<{
      invoiceId: string;
      invoiceNumber: string;
      invoiceDate: string;
      dueDate?: string;
      invoiceAmount: number;
      status: string;
    }>;
    deliveries?: Array<{
      deliveryId: string;
      deliveryNumber: string;
      scheduledDate?: string;
      actualDeliveryDate?: string;
      status: string;
      vehicleNumber?: string;
      driverName?: string;
      driverPhone?: string;
      deliveredQuantity?: number;
    }>;
    batchAllocations?: Array<{
      allocationId: number;
      batchNumber: string;
      batchId: string;
      allocatedQuantityKg: number;
      allocatedAt: string;
      qualityGrade?: string;
      productionDate?: string;
    }>;
    paymentInfo?: {
      paymentStatus: string;
      paymentTerms?: string;
      totalAmount: number;
      paidAmount: number;
      balanceDue: number;
      paymentCompletionPercentage?: number;
      paymentDueDate?: string;
      transactions?: Array<{
        transactionId: string;
        transactionDate: string;
        amount: number;
        paymentMethod: string;
        referenceNumber: string;
        status: string;
      }>;
    };
  };
  availableActions?: string[];
}

/**
 * Map backend OrderDetailsDTO to frontend OrderDetails
 */
function mapBackendOrderDetails(backend: BackendOrderDetailsDTO): OrderDetails {
  const header = backend.orderHeader;
  const timeline = backend.statusTimeline?.events || [];

  // Enhanced logging for GST data
  console.log('[OrderService] Mapping backend order:', {
    orderId: header.orderId,
    orderNumber: header.orderNumber,
    totalAmount: header.totalAmount,
    gstAmount: header.gstAmount,
    finalAmount: header.finalAmount,
    hasDeliveryInfo: !!backend.deliveryInfo,
    actualDeliveryDate: backend.deliveryInfo?.actualDeliveryDate,
  });

  // Calculate GST breakdown with proper handling
  const cgstAmount = header.cgstAmount || (header.gstAmount / 2);
  const sgstAmount = header.sgstAmount || (header.gstAmount / 2);
  const igstAmount = header.igstAmount || 0;

  return {
    id: header.orderId,
    orderNumber: header.orderNumber,
    orderDate: header.orderDate,
    orderStatus: header.status,

    customer: {
      customerId: parseInt(header.customerId) || 0,
      customerName: header.customerName,
      email: header.customerEmail,
      phone: header.customerPhone,
      gstNumber: header.customerGstin,
    },

    contract: header.contractNumber ? {
      contractId: parseInt(header.contractId || '0'),
      contractNumber: header.contractNumber,
      contractDate: '',
      contractStatus: 'ACTIVE',
    } : undefined,

    gstBreakdown: {
      subtotal: header.totalAmount - header.gstAmount,
      totalDiscount: 0,
      cgstAmount,
      sgstAmount,
      igstAmount,
      totalTax: header.gstAmount,
      totalAmount: header.finalAmount,
    },

    lineItems: backend.lineItems.map((item) => ({
      id: item.lineItemId,
      orderLineNumber: `LINE-${item.lineItemId}`,
      productType: 'BIO_CNG',
      productName: item.productName,
      quantity: item.quantity,
      quantityUnit: item.unit as any,
      unitPrice: item.unitPrice,
      discountPercentage: item.discount,
      discountAmount: item.discountAmount,
      taxBreakdown: item.taxDetails ? [
        { taxType: 'CGST' as const, rate: item.taxDetails.cgstRate, amount: item.taxDetails.cgstAmount },
        { taxType: 'SGST' as const, rate: item.taxDetails.sgstRate, amount: item.taxDetails.sgstAmount },
      ] : [],
      totalTax: item.taxDetails?.totalTax || 0,
      lineTotal: item.lineTotal,
    })),

    delivery: backend.deliveryInfo ? {
      address: {
        addressLine1: backend.deliveryInfo.deliveryAddress,
        city: '',
        state: '',
        pincode: '',
        country: 'India',
      },
      contactPerson: backend.deliveryInfo.contactPerson,
      contactPhone: backend.deliveryInfo.contactPhone,
      expectedDeliveryDate: backend.deliveryInfo.confirmedDeliveryDate || '',
      actualDeliveryDate: backend.deliveryInfo.actualDeliveryDate || undefined,
      specialInstructions: backend.deliveryInfo.specialInstructions,
    } : {
      address: {
        addressLine1: '',
        city: '',
        state: '',
        pincode: '',
        country: 'India',
      },
      contactPerson: '',
      contactPhone: '',
      expectedDeliveryDate: '',
    },

    statusTimeline: timeline.map((event) => ({
      status: event.status,
      statusLabel: event.statusLabel, // Custom label for special events like "Payment Received"
      timestamp: event.timestamp,
      updatedBy: event.performedBy,
      remarks: event.remarks,
    })),

    invoices: backend.associatedRecords?.invoices,
    deliveries: backend.associatedRecords?.deliveries,
    batchAllocations: backend.associatedRecords?.batchAllocations || [],

    payment: backend.associatedRecords?.paymentInfo ? {
      paymentStatus: backend.associatedRecords.paymentInfo.paymentStatus as any,
      totalAmount: backend.associatedRecords.paymentInfo.totalAmount,
      paidAmount: backend.associatedRecords.paymentInfo.paidAmount,
      balanceDue: backend.associatedRecords.paymentInfo.balanceDue,
      lastPaymentDate: backend.associatedRecords.paymentInfo.transactions?.[0]?.transactionDate,
      nextDueDate: backend.associatedRecords.paymentInfo.paymentDueDate,
    } : {
      paymentStatus: 'PENDING',
      totalAmount: header.finalAmount,
      paidAmount: 0,
      balanceDue: header.finalAmount,
    },

    availableActions: backend.availableActions,

    createdAt: header.orderDate,
    createdBy: 'System',
    updatedAt: header.orderDate,
    updatedBy: 'System',
  };
}

// ============================================
// API SERVICE METHODS
// ============================================

/**
 * Order Service
 */
export const orderService = {
  /**
   * Get order by ID
   *
   * @param id - Order ID
   * @returns Order details
   * @throws OrderApiError if fetch fails
   */
  async getOrderById(id: number): Promise<OrderDetails> {
    try {
      // Use the basic endpoint that works reliably
      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/${id}`;
      console.log(`[OrderService] Fetching order ${id} from:`, url);

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(),
      });

      console.log(`[OrderService] Response status for order ${id}:`, response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[OrderService] Error response:`, response.status, errorText);
        throw new OrderApiError(
          `Failed to fetch order (HTTP ${response.status}): ${errorText || response.statusText}`,
          response.status
        );
      }

      const wrappedResponse = await handleResponse<any>(response);
      console.log("[OrderService] Raw response:", wrappedResponse);

      // Handle wrapped API response {success, message, data}
      const rawOrderData = wrappedResponse.data || wrappedResponse;
      console.log("[OrderService] Extracted order data:", rawOrderData);

      // Map the response to frontend format
      const mappedOrder = mapRawOrderResponse(rawOrderData);
      console.log('[OrderService] Successfully mapped order:', mappedOrder.orderNumber);

      return mappedOrder;
    } catch (error) {
      console.error('[OrderService] Error fetching order:', error);

      if (error instanceof OrderApiError) {
        throw error;
      }

      // Handle network errors - provide demo mode
      if (error instanceof TypeError && error.message.includes('fetch')) {
        console.warn(
          'Backend API unavailable. Using demo mode for order details.'
        );
        return generateDemoOrderDetails(id);
      }

      throw new OrderApiError('Failed to fetch order details.');
    }
  },

  /**
   * Get all orders with optional filters
   *
   * @param page - Page number (0-indexed)
   * @param size - Page size
   * @param filters - Optional filters (status, customer, dates, search)
   * @returns Paginated order summaries
   * @throws OrderApiError if fetch fails
   */
  async getAllOrders(
    page: number = 0,
    size: number = 20,
    filters?: OrderFilters
  ): Promise<PagedResponse<OrderSummary>> {
    console.log('[OrderService] ========== getAllOrders START ==========');
    console.log('[OrderService] Request params:', { page, size, filters });
    console.log('[OrderService] Config:', {
      baseUrl: COMMERCE_SERVICE_BASE_URL,
      apiPath: ORDER_API_PATH,
    });

    // IMPORTANT: Check token availability (informational only, not blocking)
    const token = getAuthToken();
    console.log('[OrderService] Token status:', {
      tokenExists: !!token,
      tokenLength: token?.length || 0,
      tokenPrefix: token ? `${token.substring(0, 20)}...` : 'NONE',
    });

    try {
      // NOTE: We do NOT validate/throw here - let the API endpoint handle 401 responses
      // This allows the request to proceed and proper error handling downstream

      const queryParams = new URLSearchParams();
      queryParams.append('page', page.toString());
      queryParams.append('size', size.toString());

      if (filters) {
        // IMPORTANT: Backend API accepts SINGLE 'status' parameter (not array)
        // Frontend filter allows multiple status selection, but we send the first one to API
        // If no status filters selected, API returns all statuses (empty filter = all results)
        if (filters.status) {
          // filters.status is a single OrderStatus value
          queryParams.append('status', filters.status);
          console.log('[OrderService] Status filter applied:', filters.status);
        }
        if (filters.customerId) queryParams.append('customerId', filters.customerId.toString());
        if (filters.fromDate) queryParams.append('fromDate', filters.fromDate);
        if (filters.toDate) queryParams.append('toDate', filters.toDate);
        if (filters.searchTerm) queryParams.append('search', filters.searchTerm);
      }

      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}?${queryParams.toString()}`;
      console.log('[OrderService] Full request URL:', url);
      console.log('[OrderService] Query params:', queryParams.toString());

      const headers = getHeaders();
      console.log('[OrderService] Request headers:', {
        'Content-Type': headers['Content-Type'],
        'Authorization': headers['Authorization'] ? 'Bearer [TOKEN_PRESENT]' : 'MISSING',
      });

      console.log('[OrderService] Initiating fetch request...');
      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      console.log('[OrderService] Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: {
          contentType: response.headers.get('content-type'),
        },
      });

      const data = await handleResponse<PagedResponse<OrderSummary>>(response);
      console.log('[OrderService] ✅ Successfully fetched orders:', {
        totalElements: data.totalElements,
        totalPages: data.totalPages,
        currentPage: data.currentPage,
        contentLength: data.content.length,
      });
      console.log('[OrderService] ========== getAllOrders END (SUCCESS) ==========');

      return data;
    } catch (error) {
      console.error('[OrderService] ========== getAllOrders END (ERROR) ==========');
      console.error('[OrderService] ❌ Error in getAllOrders:', error);
      console.error('[OrderService] Error type:', error?.constructor?.name);
      console.error('[OrderService] Error stack:', error instanceof Error ? error.stack : 'No stack trace');

      if (error instanceof OrderApiError) {
        console.error('[OrderService] OrderApiError details:', {
          message: error.message,
          statusCode: error.statusCode,
          errorCode: error.errorCode,
        });
        throw error;
      }

      // Handle network errors (service not running or unreachable)
      if (error instanceof TypeError) {
        const networkErrorMsg = `Network error: Cannot reach commerce service at ${COMMERCE_SERVICE_BASE_URL}.\n\nPlease ensure:\n1. Commerce service is running on port 8083\n2. Service URL is correct in .env file (VITE_COMMERCE_SERVICE_URL)\n3. No CORS issues blocking the request\n4. JWT token is valid and not expired`;
        console.error('[OrderService] Network error details:', networkErrorMsg);
        throw new OrderApiError(
          networkErrorMsg,
          0,
          OrderErrorCode.NETWORK_ERROR
        );
      }

      // Handle timeout errors
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new OrderApiError(
          'Request timeout. The commerce service took too long to respond.',
          0,
          OrderErrorCode.TIMEOUT
        );
      }

      // Generic error
      console.error('[OrderService] Unknown error - treating as generic failure');
      throw new OrderApiError(
        'Failed to fetch orders. Please try again or contact support.',
        500,
        OrderErrorCode.UNKNOWN
      );
    }
  },

  /**
   * Create a new order from contract
   *
   * @param orderData - Order creation data (contractId, customerId, quantity, deliveryDate, notes)
   * @returns Created order details
   * @throws OrderApiError if creation fails
   */
  async createOrder(orderData: {
    contractId: number;
    customerId: number;
    quantity: number;
    requestedDeliveryDate: string;
    notes?: string;
  }): Promise<OrderDetails> {
    try {
      validateAuthToken();

      // Build query parameters for contract-based order creation
      const params = new URLSearchParams();
      params.append('contractId', orderData.contractId.toString());
      params.append('customerId', orderData.customerId.toString());
      params.append('quantity', orderData.quantity.toString());
      // Ensure date is in yyyy-MM-dd format for Spring @DateTimeFormat(iso = ISO.DATE)
      const formattedDate = orderData.requestedDeliveryDate.includes('T')
        ? orderData.requestedDeliveryDate.split('T')[0]  // Extract date part from ISO datetime
        : orderData.requestedDeliveryDate;
      params.append('requestedDeliveryDate', formattedDate);
      if (orderData.notes) {
        params.append('notes', orderData.notes);
      }

      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/from-contract?${params.toString()}`;
      console.log('[OrderService] Creating order from contract:', url);
      console.log('[OrderService] Order data:', orderData);

      const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(),
      });

      console.log('[OrderService] Create response status:', response.status);

      // The /from-contract endpoint returns {success, message, data: {...}}
      // We need to extract the data field
      if (!response.ok) {
        const errorMessage = await parseErrorResponse(response);
        console.error('[OrderService] Create order error response:', errorMessage);
        throw new OrderApiError(errorMessage || 'Failed to create order.', response.status);
      }

      const jsonResponse = await response.json();
      console.log('[OrderService] Full response:', jsonResponse);

      // Extract the order data from the wrapped response
      const responseData = jsonResponse.data;
      if (!responseData) {
        throw new OrderApiError('Invalid response: order data not found.');
      }

      const mappedOrder = mapRawOrderResponse(responseData);

      console.log('[OrderService] Order created successfully:', mappedOrder.orderNumber);
      return mappedOrder;
    } catch (error) {
      console.error('[OrderService] Error creating order:', error);

      if (error instanceof OrderApiError) {
        throw error;
      }

      throw new OrderApiError('Failed to create order.');
    }
  },

  /**
   * Update an existing order
   *
   * @param id - Order ID
   * @param updates - Partial order data to update
   * @returns Updated order details
   * @throws OrderApiError if update fails
   */
  async updateOrder(
    id: number,
    updates: {
      quantity?: number;
      requestedDeliveryDate?: string;
      notes?: string;
      status?: string;
    }
  ): Promise<OrderDetails> {
    try {
      validateAuthToken();

      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/${id}`;
      console.log('[OrderService] Updating order:', url);
      console.log('[OrderService] Update data:', updates);

      const response = await fetch(url, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(updates),
      });

      console.log('[OrderService] Update response status:', response.status);

      const wrappedResponse = await handleResponse<any>(response);
      console.log("[OrderService] Raw response:", wrappedResponse);

      // Handle wrapped API response {success, message, data}
      const rawOrderData = wrappedResponse.data || wrappedResponse;
      console.log("[OrderService] Extracted order data:", rawOrderData);
      const mappedOrder = mapRawOrderResponse(rawOrderData);

      console.log('[OrderService] Order updated successfully:', mappedOrder.orderNumber);
      return mappedOrder;
    } catch (error) {
      console.error('[OrderService] Error updating order:', error);

      if (error instanceof OrderApiError) {
        throw error;
      }

      throw new OrderApiError('Failed to update order.');
    }
  },

  /**
   * Confirm an order (PENDING → CONFIRMED)
   *
   * @param id - Order ID
   * @param remarks - Optional confirmation remarks
   * @returns Updated order details
   * @throws OrderApiError if action fails
   */
  async confirmOrder(id: number, remarks?: string): Promise<OrderDetails> {
    try {
      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/${id}/confirm`;

      const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ remarks }),
      });

      const wrappedResponse = await handleResponse<any>(response);
      console.log("[OrderService] Raw response:", wrappedResponse);

      // Handle wrapped API response {success, message, data}
      const rawOrderData = wrappedResponse.data || wrappedResponse;
      console.log("[OrderService] Extracted order data:", rawOrderData);
      return mapRawOrderResponse(rawOrderData);
    } catch (error) {
      if (error instanceof OrderApiError) {
        throw error;
      }

      throw new OrderApiError('Failed to confirm order.');
    }
  },

  /**
   * Cancel an order
   * Uses DELETE method with reason as query parameter (backend: @DeleteMapping("/{orderId}/cancel"))
   *
   * @param id - Order ID
   * @param reason - Cancellation reason
   * @returns Updated order details
   * @throws OrderApiError if action fails
   */
  async cancelOrder(id: number, reason: string): Promise<OrderDetails> {
    try {
      validateAuthToken();

      // Backend expects DELETE with reason as query parameter
      const params = new URLSearchParams();
      if (reason) {
        params.append('reason', reason);
      }
      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/${id}/cancel?${params.toString()}`;
      console.log('[OrderService] Cancelling order:', url);

      const response = await fetch(url, {
        method: 'DELETE',
        headers: getHeaders(),
      });

      console.log('[OrderService] Cancel order response status:', response.status);

      const wrappedResponse = await handleResponse<any>(response);
      console.log("[OrderService] Raw response:", wrappedResponse);

      // Handle wrapped API response {success, message, data}
      const rawOrderData = wrappedResponse.data || wrappedResponse;
      console.log("[OrderService] Extracted order data:", rawOrderData);
      return mapRawOrderResponse(rawOrderData);
    } catch (error) {
      console.error('[OrderService] Error cancelling order:', error);

      if (error instanceof OrderApiError) {
        throw error;
      }

      throw new OrderApiError('Failed to cancel order.');
    }
  },

  /**
   * Mark order as ready for dispatch
   *
   * @param id - Order ID
   * @returns Updated order details
   * @throws OrderApiError if action fails
   */
  async markReady(id: number): Promise<OrderDetails> {
    try {
      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/${id}/ready`;

      const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(),
      });

      const wrappedResponse = await handleResponse<any>(response);
      console.log("[OrderService] Raw response:", wrappedResponse);

      // Handle wrapped API response {success, message, data}
      const rawOrderData = wrappedResponse.data || wrappedResponse;
      console.log("[OrderService] Extracted order data:", rawOrderData);
      return mapRawOrderResponse(rawOrderData);
    } catch (error) {
      if (error instanceof OrderApiError) {
        throw error;
      }

      throw new OrderApiError('Failed to mark order as ready.');
    }
  },

  /**
   * Schedule delivery for an order
   *
   * @param id - Order ID
   * @param deliveryDate - Scheduled delivery date
   * @returns Updated order details
   * @throws OrderApiError if action fails
   */
  async scheduleDelivery(
    id: number,
    deliveryDate: string
  ): Promise<OrderDetails> {
    try {
      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/${id}/schedule-delivery`;

      const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ deliveryDate }),
      });

      const wrappedResponse = await handleResponse<any>(response);
      console.log("[OrderService] Raw response:", wrappedResponse);

      // Handle wrapped API response {success, message, data}
      const rawOrderData = wrappedResponse.data || wrappedResponse;
      console.log("[OrderService] Extracted order data:", rawOrderData);
      return mapRawOrderResponse(rawOrderData);
    } catch (error) {
      if (error instanceof OrderApiError) {
        throw error;
      }

      throw new OrderApiError('Failed to schedule delivery.');
    }
  },

  /**
   * Delete an order
   *
   * @param id - Order ID
   * @returns void on success
   * @throws OrderApiError if deletion fails
   */
  async deleteOrder(id: number): Promise<void> {
    try {
      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/${id}`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: getHeaders(),
      });

      // Handle response - DELETE typically returns 204 No Content or 200 OK with empty body
      if (!response.ok) {
        throw new OrderApiError(`Failed to delete order: ${response.statusText}`);
      }
    } catch (error) {
      if (error instanceof OrderApiError) {
        throw error;
      }

      throw new OrderApiError('Failed to delete order.');
    }
  },

  /**
   * Create a product-based order (multiple products with quantities)
   *
   * @param orderData - Order creation data (customerId, lineItems, expectedDeliveryDate, specialInstructions)
   * @returns Created order details
   * @throws OrderApiError if creation fails
   */
  async createProductBasedOrder(orderData: {
    customerId: number;
    lineItems: Array<{
      productId: number;
      quantity: string;  // BigDecimal as string for proper JSON serialization
      unit: string;
      unitPrice: string;  // BigDecimal as string for proper JSON serialization
    }>;
    expectedDeliveryDate: string;
    deliveryAddress?: string;  // Optional - uses buyer's registered address if not provided
    specialInstructions?: string;
  }): Promise<OrderDetails> {
    try {
      validateAuthToken();

      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/product-based`;
      console.log('[OrderService] Creating product-based order:', url);
      console.log('[OrderService] Order data:', orderData);

      const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(orderData),
      });

      console.log('[OrderService] Create product-based order response status:', response.status);

      // Handle error responses
      if (!response.ok) {
        const responseText = await response.text();
        console.error('[OrderService] Create product-based order error raw response:', responseText);

        // Handle 401 Unauthorized - session expired
        if (response.status === 401) {
          console.error('[OrderService] Session expired - clearing auth and redirecting to login');
          localStorage.removeItem('saubhagya_jwt_token');
          localStorage.removeItem('user');
          window.location.href = '/login';
          throw new OrderApiError('Session expired. Please login again.', 401);
        }

        const errorMessage = await parseErrorResponse(response);
        console.error('[OrderService] Create product-based order error message:', errorMessage);
        throw new OrderApiError(errorMessage || 'Failed to create product-based order.', response.status);
      }

      const jsonResponse = await response.json();
      console.log('[OrderService] Full response:', jsonResponse);

      // Extract the order data from the wrapped response
      const responseData = jsonResponse.data;
      if (!responseData) {
        throw new OrderApiError('Invalid response: order data not found.');
      }

      const mappedOrder = mapRawOrderResponse(responseData);

      console.log('[OrderService] Product-based order created successfully:', mappedOrder.orderNumber);
      return mappedOrder;
    } catch (error) {
      console.error('[OrderService] Error creating product-based order:', error);

      if (error instanceof OrderApiError) {
        throw error;
      }

      throw new OrderApiError('Failed to create product-based order.');
    }
  },

  /**
   * Check if service is available
   *
   * @returns true if service is available
   */
  async checkHealth(): Promise<boolean> {
    try {
      const response = await fetch(
        `${COMMERCE_SERVICE_BASE_URL}/actuator/health`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );
      return response.ok;
    } catch {
      return false;
    }
  },

  // ==================== TRANSPORT ENDPOINTS ====================

  /**
   * Get all available transport units
   *
   * @returns List of transport units
   * @throws OrderApiError if fetch fails
   */
  async getTransportUnits(): Promise<TransportUnit[]> {
    try {
      validateAuthToken();

      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/transport-units`;
      console.log('[OrderService] Fetching transport units from:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(),
      });

      console.log('[OrderService] Transport units response status:', response.status);

      const wrappedResponse = await handleResponse<any>(response);
      const units = wrappedResponse.data || wrappedResponse;

      console.log('[OrderService] Successfully fetched transport units:', units.length);
      return units;
    } catch (error) {
      console.error('[OrderService] Error fetching transport units:', error);

      if (error instanceof OrderApiError) {
        throw error;
      }

      throw new OrderApiError('Failed to fetch transport units.');
    }
  },

  /**
   * Create transport request for an order
   *
   * @param orderId - Order ID
   * @param request - Transport request details
   * @returns Updated order details
   * @throws OrderApiError if creation fails
   */
  async createTransportRequest(
    orderId: number,
    request: CreateTransportRequestData
  ): Promise<OrderDetails> {
    try {
      validateAuthToken();

      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/${orderId}/create-transport-request`;
      console.log('[OrderService] Creating transport request for order:', orderId);
      console.log('[OrderService] Request data:', request);

      const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(request),
      });

      console.log('[OrderService] Create transport request response status:', response.status);

      if (!response.ok) {
        const errorMessage = await parseErrorResponse(response);
        throw new OrderApiError(errorMessage || 'Failed to create transport request.', response.status);
      }

      const wrappedResponse = await response.json();
      const rawOrderData = wrappedResponse.data || wrappedResponse;
      const mappedOrder = mapRawOrderResponse(rawOrderData);

      console.log('[OrderService] Transport request created successfully for order:', mappedOrder.orderNumber);
      return mappedOrder;
    } catch (error) {
      console.error('[OrderService] Error creating transport request:', error);

      if (error instanceof OrderApiError) {
        throw error;
      }

      throw new OrderApiError('Failed to create transport request.');
    }
  },

  /**
   * Link existing transport request to an order
   * Used when order has TRANSPORT_REQUESTED status but transport_request_id is null
   *
   * @param orderId - Order ID
   * @returns Updated order details with linked transport
   * @throws OrderApiError if linking fails
   */
  async linkExistingTransport(orderId: number): Promise<OrderDetails> {
    try {
      validateAuthToken();

      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/${orderId}/link-transport`;
      console.log('[OrderService] Linking existing transport for order:', orderId);

      const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(),
      });

      console.log('[OrderService] Link transport response status:', response.status);

      if (!response.ok) {
        const errorMessage = await parseErrorResponse(response);
        throw new OrderApiError(errorMessage || 'Failed to link transport request.', response.status);
      }

      const wrappedResponse = await response.json();
      const rawOrderData = wrappedResponse.data || wrappedResponse;
      const mappedOrder = mapRawOrderResponse(rawOrderData);

      console.log('[OrderService] Transport linked successfully for order:', mappedOrder.orderNumber);
      return mappedOrder;
    } catch (error) {
      console.error('[OrderService] Error linking transport:', error);

      if (error instanceof OrderApiError) {
        throw error;
      }

      throw new OrderApiError('Failed to link transport request.');
    }
  },

  /**
   * Get transport details for an order
   * Returns transport request info and auto-links if needed
   *
   * @param orderId - Order ID
   * @returns Transport details or null if not found
   */
  async getOrderTransport(orderId: number): Promise<{
    hasTransport: boolean;
    transportRequestId?: number;
    transportRequestNumber?: string;
    transportStatus?: string;
    autoLinked?: boolean;
  } | null> {
    try {
      validateAuthToken();

      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/${orderId}/transport`;
      console.log('[OrderService] Getting transport for order:', orderId);

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(),
      });

      if (!response.ok) {
        console.warn('[OrderService] Get transport failed:', response.status);
        return null;
      }

      const wrappedResponse = await response.json();
      return wrappedResponse.data || null;
    } catch (error) {
      console.error('[OrderService] Error getting transport:', error);
      return null;
    }
  },

  /**
   * Sync transport status for an order
   * Fetches current transport status from transport-service and updates order accordingly
   *
   * @param orderId - Order ID
   * @returns Updated order details with synced transport status
   * @throws OrderApiError if sync fails
   */
  async syncTransportStatus(orderId: number): Promise<OrderDetails> {
    try {
      validateAuthToken();

      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/${orderId}/sync-transport-status`;
      console.log('[OrderService] Syncing transport status for order:', orderId);

      const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(),
      });

      console.log('[OrderService] Sync transport response status:', response.status);

      if (!response.ok) {
        const errorMessage = await parseErrorResponse(response);
        throw new OrderApiError(errorMessage || 'Failed to sync transport status.', response.status);
      }

      const wrappedResponse = await response.json();
      const rawOrderData = wrappedResponse.data || wrappedResponse;
      const mappedOrder = mapRawOrderResponse(rawOrderData);

      console.log('[OrderService] Transport status synced for order:', mappedOrder.orderNumber);
      return mappedOrder;
    } catch (error) {
      console.error('[OrderService] Error syncing transport status:', error);

      if (error instanceof OrderApiError) {
        throw error;
      }

      throw new OrderApiError('Failed to sync transport status.');
    }
  },

  // ==================== PAYMENT ENDPOINTS ====================

  /**
   * Generate payment link for an order (Cashfree integration)
   * Sends payment link via email to the buyer
   *
   * @param orderId - Order ID
   * @returns Payment link response with orderId, orderNumber, paymentLink, cashfreeOrderId
   * @throws OrderApiError if generation fails
   */
  async generatePaymentLink(orderId: number): Promise<PaymentLinkResponse> {
    try {
      validateAuthToken();

      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/${orderId}/generate-payment-link`;
      console.log('[OrderService] Generating payment link for order:', orderId);

      const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(),
      });

      console.log('[OrderService] Generate payment link response status:', response.status);

      if (!response.ok) {
        const errorMessage = await parseErrorResponse(response);
        throw new OrderApiError(errorMessage || 'Failed to generate payment link.', response.status);
      }

      const wrappedResponse = await response.json();
      const paymentData = wrappedResponse.data || wrappedResponse;

      console.log('[OrderService] Payment link generated successfully:', paymentData.paymentLink);
      return paymentData as PaymentLinkResponse;
    } catch (error) {
      console.error('[OrderService] Error generating payment link:', error);

      if (error instanceof OrderApiError) {
        throw error;
      }

      throw new OrderApiError('Failed to generate payment link.');
    }
  },

  /**
   * Get payment status for an order
   *
   * @param orderId - Order ID
   * @returns Payment status response with current payment status, link, and amount details
   * @throws OrderApiError if fetch fails
   */
  async getPaymentStatus(orderId: number): Promise<PaymentStatusResponse> {
    try {
      validateAuthToken();

      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/${orderId}/payment-status`;
      console.log('[OrderService] Fetching payment status for order:', orderId);

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(),
      });

      console.log('[OrderService] Payment status response status:', response.status);

      if (!response.ok) {
        const errorMessage = await parseErrorResponse(response);
        throw new OrderApiError(errorMessage || 'Failed to fetch payment status.', response.status);
      }

      const wrappedResponse = await response.json();
      const paymentData = wrappedResponse.data || wrappedResponse;

      console.log('[OrderService] Payment status fetched successfully:', paymentData.paymentStatus);
      return paymentData as PaymentStatusResponse;
    } catch (error) {
      console.error('[OrderService] Error fetching payment status:', error);

      if (error instanceof OrderApiError) {
        throw error;
      }

      throw new OrderApiError('Failed to fetch payment status.');
    }
  },

  /**
   * Check and sync payment status from Cashfree
   * Used for local testing when webhooks can't reach localhost
   *
   * @param orderId - Order ID
   * @returns Payment sync response with updated status and transport request info
   * @throws OrderApiError if sync fails
   */
  async checkAndSyncPaymentStatus(orderId: number): Promise<CheckPaymentSyncResponse> {
    try {
      validateAuthToken();

      const url = `${COMMERCE_SERVICE_BASE_URL}/api/v1/payments/cashfree/check-status/${orderId}`;
      console.log('[OrderService] Checking and syncing payment status for order:', orderId);

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(),
      });

      console.log('[OrderService] Check payment sync response status:', response.status);

      if (!response.ok) {
        const errorMessage = await parseErrorResponse(response);
        throw new OrderApiError(errorMessage || 'Failed to sync payment status.', response.status);
      }

      const wrappedResponse = await response.json();
      const syncData = wrappedResponse.data || wrappedResponse;

      console.log('[OrderService] Payment sync completed:', syncData);
      return syncData as CheckPaymentSyncResponse;
    } catch (error) {
      console.error('[OrderService] Error syncing payment status:', error);

      if (error instanceof OrderApiError) {
        throw error;
      }

      throw new OrderApiError('Failed to sync payment status from Cashfree.');
    }
  },

  /**
   * Mark payment as paid (FOR TESTING ONLY)
   * Manually marks payment as complete for local testing
   *
   * @param orderId - Order ID
   * @returns Payment mark response with updated status
   * @throws OrderApiError if marking fails
   */
  async markPaymentAsPaid(orderId: number): Promise<CheckPaymentSyncResponse> {
    try {
      validateAuthToken();

      const url = `${COMMERCE_SERVICE_BASE_URL}/api/v1/payments/cashfree/mark-paid/${orderId}`;
      console.log('[OrderService] Marking payment as paid for order:', orderId);

      const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(),
      });

      console.log('[OrderService] Mark payment response status:', response.status);

      if (!response.ok) {
        const errorMessage = await parseErrorResponse(response);
        throw new OrderApiError(errorMessage || 'Failed to mark payment as paid.', response.status);
      }

      const wrappedResponse = await response.json();
      const markData = wrappedResponse.data || wrappedResponse;

      console.log('[OrderService] Payment marked as paid:', markData);
      return markData as CheckPaymentSyncResponse;
    } catch (error) {
      console.error('[OrderService] Error marking payment as paid:', error);

      if (error instanceof OrderApiError) {
        throw error;
      }

      throw new OrderApiError('Failed to mark payment as paid.');
    }
  },

  /**
   * Auto-create transport request for an order (without unit selection)
   * Creates transport request automatically for paid orders
   *
   * @param orderId - Order ID
   * @returns Transport creation response
   * @throws OrderApiError if creation fails
   */
  async autoCreateTransportRequest(orderId: number): Promise<AutoCreateTransportResponse> {
    try {
      validateAuthToken();

      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/${orderId}/auto-create-transport`;
      console.log('[OrderService] Auto-creating transport request for order:', orderId);

      const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(),
      });

      console.log('[OrderService] Auto-create transport response status:', response.status);

      if (!response.ok) {
        const errorMessage = await parseErrorResponse(response);
        throw new OrderApiError(errorMessage || 'Failed to create transport request.', response.status);
      }

      const wrappedResponse = await response.json();
      const transportData = wrappedResponse.data || wrappedResponse;

      console.log('[OrderService] Transport request created:', transportData);
      return transportData as AutoCreateTransportResponse;
    } catch (error) {
      console.error('[OrderService] Error creating transport request:', error);

      if (error instanceof OrderApiError) {
        throw error;
      }

      throw new OrderApiError('Failed to create transport request.');
    }
  },

  // ==================== APPROVAL WORKFLOW METHODS ====================

  /**
   * Get approval status for an order
   *
   * @param orderId - Order ID
   * @returns Approval status details
   * @throws OrderApiError if fetch fails
   */
  async getApprovalStatus(orderId: number): Promise<ApprovalStatusResponse> {
    try {
      validateAuthToken();

      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/${orderId}`;
      console.log('[OrderService] Fetching approval status for order:', orderId);

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(),
      });

      const wrappedResponse = await handleResponse<any>(response);
      const orderData = wrappedResponse.data || wrappedResponse;

      return {
        orderId: orderData.id,
        orderNumber: orderData.orderNumber,
        orderStatus: orderData.orderStatus,
        approvalStatus: orderData.approvalStatus,
        approvalRequestedAt: orderData.approvalRequestedAt,
        approvalApprovedAt: orderData.approvalApprovedAt,
        approvalApprovedByEmail: orderData.approvalApprovedByEmail,
        approvalTokenExpiry: orderData.approvalTokenExpiry,
        approvalRejectionReason: orderData.approvalRejectionReason,
      };
    } catch (error) {
      console.error('[OrderService] Error fetching approval status:', error);
      if (error instanceof OrderApiError) {
        throw error;
      }
      throw new OrderApiError('Failed to fetch approval status.');
    }
  },

  /**
   * Admin approve delivery for an order
   *
   * @param orderId - Order ID
   * @param notes - Optional admin notes
   * @returns Approval response
   * @throws OrderApiError if approval fails
   */
  async adminApproveDelivery(orderId: number, notes?: string): Promise<ApprovalResponse> {
    try {
      validateAuthToken();

      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/${orderId}/admin-approve`;
      console.log('[OrderService] Admin approving delivery for order:', orderId);

      const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ notes }),
      });

      const wrappedResponse = await handleResponse<any>(response);
      const approvalData = wrappedResponse.data || wrappedResponse;

      console.log('[OrderService] Admin approval successful:', approvalData);
      return approvalData as ApprovalResponse;
    } catch (error) {
      console.error('[OrderService] Error in admin approval:', error);
      if (error instanceof OrderApiError) {
        throw error;
      }
      throw new OrderApiError('Failed to approve delivery.');
    }
  },

  /**
   * Resend approval email for an order
   *
   * @param orderId - Order ID
   * @returns Success response with email details
   * @throws OrderApiError if resend fails
   */
  async resendApprovalEmail(orderId: number): Promise<ResendEmailResponse> {
    try {
      validateAuthToken();

      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/${orderId}/resend-approval-email`;
      console.log('[OrderService] Resending approval email for order:', orderId);

      const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(),
      });

      const wrappedResponse = await handleResponse<any>(response);
      const emailData = wrappedResponse.data || wrappedResponse;

      console.log('[OrderService] Approval email resent:', emailData);
      return emailData as ResendEmailResponse;
    } catch (error) {
      console.error('[OrderService] Error resending approval email:', error);
      if (error instanceof OrderApiError) {
        throw error;
      }
      throw new OrderApiError('Failed to resend approval email.');
    }
  },

  /**
   * Get approval audit history for an order
   *
   * @param orderId - Order ID
   * @param page - Page number (0-based)
   * @param size - Page size
   * @returns Paged approval audit history
   * @throws OrderApiError if fetch fails
   */
  async getApprovalHistory(orderId: number, page = 0, size = 20): Promise<PagedApprovalHistory> {
    try {
      validateAuthToken();

      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/${orderId}/approval-history?page=${page}&size=${size}`;
      console.log('[OrderService] Fetching approval history for order:', orderId);

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(),
      });

      const wrappedResponse = await handleResponse<any>(response);
      const historyData = wrappedResponse.data || wrappedResponse;

      console.log('[OrderService] Approval history fetched:', historyData);
      return historyData as PagedApprovalHistory;
    } catch (error) {
      console.error('[OrderService] Error fetching approval history:', error);
      if (error instanceof OrderApiError) {
        throw error;
      }
      throw new OrderApiError('Failed to fetch approval history.');
    }
  },

  /**
   * Customer approve delivery via token (public endpoint)
   * Note: This doesn't require auth token - uses approval token from email
   *
   * @param orderId - Order ID
   * @param token - Approval token from email
   * @returns Approval response
   * @throws OrderApiError if approval fails
   */
  async customerApproveDelivery(orderId: number, token: string): Promise<ApprovalResponse> {
    try {
      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/${orderId}/approve-delivery?token=${encodeURIComponent(token)}`;
      console.log('[OrderService] Customer approving delivery for order:', orderId);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const wrappedResponse = await handleResponse<any>(response);
      const approvalData = wrappedResponse.data || wrappedResponse;

      console.log('[OrderService] Customer approval successful:', approvalData);
      return approvalData as ApprovalResponse;
    } catch (error) {
      console.error('[OrderService] Error in customer approval:', error);
      if (error instanceof OrderApiError) {
        throw error;
      }
      throw new OrderApiError('Failed to approve delivery.');
    }
  },
};

// ==================== APPROVAL WORKFLOW TYPES ====================

/**
 * Approval status response
 */
export interface ApprovalStatusResponse {
  orderId: number;
  orderNumber: string;
  orderStatus: string;
  approvalStatus?: string;
  approvalRequestedAt?: string;
  approvalApprovedAt?: string;
  approvalApprovedByEmail?: string;
  approvalTokenExpiry?: string;
  approvalRejectionReason?: string;
}

/**
 * Approval response from approve endpoints
 */
export interface ApprovalResponse {
  orderId: number;
  orderNumber: string;
  approvalStatus: string;
  previousOrderStatus: string;
  newOrderStatus: string;
  approvalRequestedAt?: string;
  approvalApprovedAt?: string;
  approvedBy?: string;
  emailInvoiceSent?: boolean;
  message: string;
}

/**
 * Resend email response
 */
export interface ResendEmailResponse {
  orderId: number;
  orderNumber: string;
  emailSentTo: string;
  newTokenExpiry: string;
  message: string;
}

/**
 * Approval audit history item
 */
export interface ApprovalHistoryItem {
  auditId: number;
  orderId: number;
  eventType: string;
  performedByEmail?: string;
  performedByUserId?: string;
  previousStatus?: string;
  newStatus?: string;
  notes?: string;
  createdAt: string;
}

/**
 * Paged approval history response
 */
export interface PagedApprovalHistory {
  content: ApprovalHistoryItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

// ==================== TYPES ====================

/**
 * Transport Unit from transport-service
 */
export interface TransportUnit {
  id: number;
  unitCode: string;
  unitName: string;
  address: string;
  contactNumber?: string;
  email?: string;
  latitude?: number;
  longitude?: number;
  coverageRadiusKm?: number;
  driverCount?: number;
  vehicleCount?: number;
}

/**
 * Request data for creating a transport request
 */
export interface CreateTransportRequestData {
  transportUnitId: number;
  pickupAddress?: string;
  pickupCity?: string;
  pickupState?: string;
  pickupPincode?: string;
  pickupLatitude?: number;
  pickupLongitude?: number;
  pickupContactName?: string;
  pickupContactPhone?: string;
  dropAddress?: string;
  dropCity?: string;
  dropState?: string;
  dropPincode?: string;
  dropLatitude?: number;
  dropLongitude?: number;
  dropContactName?: string;
  dropContactPhone?: string;
  productDescription?: string;
  quantity?: number;
  weightKg?: number;
  priorityId?: number;
  isUrgent?: boolean;
  notes?: string;
}

/**
 * Response from check-status and mark-paid endpoints
 */
export interface CheckPaymentSyncResponse {
  orderId: number;
  orderNumber: string;
  currentPaymentStatus?: string;
  currentOrderStatus?: string;
  cashfreeLinkStatus?: string;
  paymentUpdated?: boolean;
  newPaymentStatus?: string;
  transportRequestCreated?: boolean;
  transportError?: string;
  message?: string;
  hint?: string;
  error?: string;
  paymentStatus?: string;
  paymentCompletedAt?: string;
}

/**
 * Response from auto-create-transport endpoint
 */
export interface AutoCreateTransportResponse {
  orderId: number;
  orderNumber: string;
  paymentStatus: string;
  orderStatus: string;
  transportRequestId?: number;
  transportRequestNumber?: string;
  transportStatus?: string;
  success?: boolean;
  message?: string;
  hint?: string;
  error?: string;
  error?: string;
}

export default orderService;
